import { useState, useCallback } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Upload,
  FileSpreadsheet,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
  AlertCircle,
  Users,
  Briefcase,
  Target,
  ChevronDown,
  Loader2,
  Download,
  RefreshCw,
  CheckCircle2,
  XCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";

const DATA_TYPES = {
  clients: { label: "Clients", icon: Users, description: "Import your client contacts" },
  projects: { label: "Projects", icon: Briefcase, description: "Import project data" },
  deals: { label: "CRM Deals", icon: Target, description: "Import pipeline deals" },
};

const STEPS = {
  UPLOAD: 0,
  MAP: 1,
  PREVIEW: 2,
  RESULT: 3,
};

export default function CSVImportModal({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [loading, setLoading] = useState(false);
  const [dataType, setDataType] = useState("deals");
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [mappings, setMappings] = useState({});
  const [options, setOptions] = useState({
    skip_duplicates: true,
    update_existing: false,
  });
  const [importResult, setImportResult] = useState(null);

  const resetState = () => {
    setStep(STEPS.UPLOAD);
    setFile(null);
    setCsvData(null);
    setPreviewData(null);
    setMappings({});
    setImportResult(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error("Please upload a CSV file");
        return;
      }
      setFile(selectedFile);
      
      // Read file for later use
      const reader = new FileReader();
      reader.onload = (event) => {
        setCsvData(event.target.result);
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("data_type", dataType);

      const response = await api.post("/import/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setPreviewData(response.data);
      
      // Set suggested mappings
      const suggestedMappings = response.data.suggested_mappings[dataType] || {};
      setMappings(suggestedMappings);
      
      // If we detected a different type, suggest it
      if (response.data.detected_type !== dataType) {
        toast.info(`Detected ${DATA_TYPES[response.data.detected_type]?.label || response.data.detected_type} data. You can change the type if needed.`);
      }
      
      setStep(STEPS.MAP);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process CSV");
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (field, column) => {
    setMappings((prev) => ({
      ...prev,
      [field]: column === "_none_" ? null : column,
    }));
  };

  const handleExecuteImport = async () => {
    setLoading(true);
    try {
      const response = await api.post("/import/execute", {
        csv_data: csvData,
        data_type: dataType,
        mappings: mappings,
        options: options,
      });

      setImportResult(response.data.results);
      setStep(STEPS.RESULT);
      
      if (response.data.results.imported > 0) {
        toast.success(`Successfully imported ${response.data.results.imported} ${dataType}!`);
        onSuccess?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = (type) => {
    const templates = {
      clients: "Name,Email,Company,Phone,Notes,Website,Tags\nJohn Smith,john@example.com,Acme Corp,+1-555-0123,VIP client,https://acme.com,\"vip,priority\"",
      projects: "Name,Client,Status,Value,Description,Start Date,End Date,Tags\nWebsite Redesign,Acme Corp,In Progress,5000,Complete website overhaul,2024-01-15,2024-03-30,\"web,design\"",
      deals: "Name,Value,Stage,Contact Name,Contact Email,Company,Notes,Source,Close Date\nEnterprise Deal,25000,Proposal,Jane Doe,jane@bigcorp.com,BigCorp Inc,Hot lead from referral,Referral,2024-02-28",
    };

    const blob = new Blob([templates[type]], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${type}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const availableFields = previewData?.available_fields?.[dataType] || [];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-800">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-indigo-400" />
            Import Data from CSV
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Migrate your data from And.co or other platforms
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6 px-4">
          {["Upload", "Map Fields", "Preview", "Done"].map((label, idx) => (
            <div key={label} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step > idx
                    ? "bg-green-500 text-white"
                    : step === idx
                    ? "bg-indigo-500 text-white"
                    : "bg-slate-700 text-slate-400"
                }`}
              >
                {step > idx ? <Check className="w-4 h-4" /> : idx + 1}
              </div>
              <span className={`ml-2 text-sm ${step >= idx ? "text-white" : "text-slate-500"}`}>
                {label}
              </span>
              {idx < 3 && <div className="w-8 h-px bg-slate-700 mx-2" />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === STEPS.UPLOAD && (
          <div className="space-y-6">
            {/* Data Type Selection */}
            <div className="space-y-3">
              <Label className="text-white">What are you importing?</Label>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(DATA_TYPES).map(([key, { label, icon: Icon, description }]) => (
                  <button
                    key={key}
                    onClick={() => setDataType(key)}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      dataType === key
                        ? "border-indigo-500 bg-indigo-500/10"
                        : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
                    }`}
                  >
                    <Icon className={`w-5 h-5 mb-2 ${dataType === key ? "text-indigo-400" : "text-slate-400"}`} />
                    <p className="font-medium text-white">{label}</p>
                    <p className="text-xs text-slate-400 mt-1">{description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-3">
              <Label className="text-white">Upload CSV File</Label>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  file ? "border-green-500 bg-green-500/5" : "border-slate-700 hover:border-slate-600"
                }`}
              >
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  {file ? (
                    <div className="flex flex-col items-center">
                      <CheckCircle2 className="w-10 h-10 text-green-500 mb-2" />
                      <p className="text-green-400 font-medium">{file.name}</p>
                      <p className="text-slate-500 text-sm mt-1">Click to change file</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="w-10 h-10 text-slate-500 mb-2" />
                      <p className="text-slate-300">Click to upload or drag and drop</p>
                      <p className="text-slate-500 text-sm mt-1">CSV files only</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Download Template */}
            <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
              <div>
                <p className="text-sm text-white">Need a template?</p>
                <p className="text-xs text-slate-400">Download a sample CSV to get started</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadTemplate(dataType)}
                className="border-slate-700 text-slate-300 hover:text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleUpload}
                disabled={!file || loading}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Map Fields */}
        {step === STEPS.MAP && previewData && (
          <div className="space-y-6">
            <Alert className="bg-slate-800 border-slate-700">
              <AlertCircle className="w-4 h-4 text-indigo-400" />
              <AlertDescription className="text-slate-300">
                Found <strong>{previewData.total_rows}</strong> rows. Map your CSV columns to ClientKit fields below.
              </AlertDescription>
            </Alert>

            {/* Field Mappings */}
            <div className="space-y-4">
              <Label className="text-white">Column Mapping</Label>
              <div className="grid gap-3">
                {availableFields.map((field) => (
                  <div key={field} className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-lg">
                    <div className="w-1/3">
                      <p className="text-white capitalize font-medium">
                        {field.replace(/_/g, " ")}
                        {["name", "email", "value"].includes(field) && (
                          <span className="text-red-400 ml-1">*</span>
                        )}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-500" />
                    <div className="flex-1">
                      <Select
                        value={mappings[field] || "_none_"}
                        onValueChange={(value) => handleMappingChange(field, value)}
                      >
                        <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="_none_" className="text-slate-400">
                            -- Don't import --
                          </SelectItem>
                          {previewData.headers.map((header) => (
                            <SelectItem key={header} value={header} className="text-white">
                              {header}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Options */}
            <div className="space-y-3 p-4 bg-slate-800/50 rounded-lg">
              <Label className="text-white">Import Options</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="skip_duplicates"
                    checked={options.skip_duplicates}
                    onCheckedChange={(checked) =>
                      setOptions((prev) => ({ ...prev, skip_duplicates: checked }))
                    }
                  />
                  <label htmlFor="skip_duplicates" className="text-sm text-slate-300">
                    Skip duplicate entries
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="update_existing"
                    checked={options.update_existing}
                    onCheckedChange={(checked) =>
                      setOptions((prev) => ({ ...prev, update_existing: checked }))
                    }
                  />
                  <label htmlFor="update_existing" className="text-sm text-slate-300">
                    Update existing records if found
                  </label>
                </div>
              </div>
            </div>

            {/* Preview Table */}
            {previewData.sample_data?.length > 0 && (
              <div className="space-y-3">
                <Label className="text-white">Preview (First 5 rows)</Label>
                <div className="overflow-x-auto rounded-lg border border-slate-700">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-800">
                      <tr>
                        {previewData.headers.map((header) => (
                          <th key={header} className="px-3 py-2 text-left text-slate-300 font-medium">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.sample_data.map((row, idx) => (
                        <tr key={idx} className="border-t border-slate-700">
                          {previewData.headers.map((header) => (
                            <td key={header} className="px-3 py-2 text-slate-400 max-w-[200px] truncate">
                              {row[header] || "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setStep(STEPS.UPLOAD)}
                className="border-slate-700 text-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleExecuteImport}
                disabled={loading}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Import {previewData.total_rows} Records
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {step === STEPS.RESULT && importResult && (
          <div className="space-y-6">
            <div className="text-center py-6">
              {importResult.imported > 0 ? (
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              ) : (
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              )}
              <h3 className="text-2xl font-bold text-white mb-2">
                {importResult.imported > 0 ? "Import Complete!" : "Import Failed"}
              </h3>
              <p className="text-slate-400">
                {importResult.imported > 0
                  ? `Successfully imported ${importResult.imported} ${dataType}`
                  : "No records were imported"}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-green-500/10 border-green-500/30">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">{importResult.imported}</p>
                  <p className="text-sm text-green-300">Imported</p>
                </CardContent>
              </Card>
              <Card className="bg-yellow-500/10 border-yellow-500/30">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-400">{importResult.skipped}</p>
                  <p className="text-sm text-yellow-300">Skipped</p>
                </CardContent>
              </Card>
            </div>

            {importResult.errors?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-white">Errors ({importResult.errors.length})</Label>
                <div className="max-h-40 overflow-y-auto bg-slate-800/50 rounded-lg p-3 space-y-1">
                  {importResult.errors.slice(0, 10).map((error, idx) => (
                    <p key={idx} className="text-sm text-red-400">
                      {error}
                    </p>
                  ))}
                  {importResult.errors.length > 10 && (
                    <p className="text-sm text-slate-500">
                      ...and {importResult.errors.length - 10} more errors
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={resetState}
                className="border-slate-700 text-slate-300"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Import More
              </Button>
              <Button onClick={handleClose} className="bg-indigo-600 hover:bg-indigo-700">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
